/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
/* eslint-disable max-len */
// 导出Provider
import { BaseProvider } from '@dm/live-material-infigo';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { type SearchData } from '../global-search-str.service';
import { conditionSearch } from './conditionalSearch';
import { checkLicense } from './conditionalSearch/grammar.util';
import { SourceTypeWithLicense } from './const';
import { equalAPI } from './utils';

const { SyncProvider, AsyncProvider } = BaseProvider;

export class PageProvider extends SyncProvider {
    static type = 'Page';
    static index = 1;
    filter = (v: { show: boolean }): boolean => v && v.show !== false;
}

export class OperationProvider extends SyncProvider {
    static type = 'Operation';
    static index = 2;
    filter = (v: { show: boolean }): boolean => v && v.show !== false;
}

export class HelpProvider extends SyncProvider {
    static type = 'Help';
    static index = 3;
    static matchKey = 'name';
    filter = (v: { show: boolean }): boolean => v && v.show !== false;
}

export class SourceProvider extends AsyncProvider {
    static type = 'source';
    static index = 4;

    constructor(request: { (value: any, range?: any): Observable<SearchData[]>; (value: string, params?: any): Observable<object> }) {
        super();
        this.request = request;
    }

    filter = (v: { show: boolean }): boolean => v && v.show !== false;
}


export class ConditionalSearchProvider extends AsyncProvider {
    static index = 5;
    static type = 'source_condition';
    preObserver;
    conditionSearch;
    extraProvider;
    preResult = {
        data: [],
        more: false,
    };

    constructor(language, request, i18n, licenseCheck) {
        super();

        const keys = checkLicense(licenseCheck, SourceTypeWithLicense[i18n.getLanguage() === 'ja' ? 'en' : i18n.getLanguage()]).map((i) => i.name || i);

        const reg = new RegExp(`^(${keys.join('|')})\\s`, 'i');
        this.use = this.monopolize = reg;
        this.conditionSearch = conditionSearch(language === 'ja' ? 'en' : language, i18n);
        this.request = request;
    }

    filter = (v: { show: boolean }): boolean => v && v.show !== false;

    search(value: string): Observable<unknown> {
        this.breakOff();
        this.preRange = this.initRange;
        const API = this.conditionSearch(value);
        if (API && !equalAPI(this.preValue, API)) {
            return this.preObserver = new Observable((o) => {
                this.observable = o;
                this.timer = setTimeout(() => {
                    this.request(API, this.generateRange(this.preRange))
                        .pipe(
                            map((val: Array<any>) => ({
                                data: val.slice(0, this.displayNum).filter(this.filter),
                                more: val.length > this.displayNum,
                            })),
                        )
                        .subscribe((re: { data: Array<any>; more: boolean }) => {
                            this.preValue = API;
                            this.preResult = re;
                            o.next(re);
                        });
                }, this.delay);
            });
        }
        return this.preObserver = new Observable((o) => {
            this.observable = o;
            o.next(this.preResult);
        });
    }

    loadForMore(): void {
        if (this.preResult.more) {
            this.preRange += this.displayNum;
            this.request(this.preValue, this.generateRange(this.preRange)).pipe(
                map((val: Array<any>) => ({
                    data: val.slice(0, this.displayNum).filter(this.filter),
                    more: val.length > this.displayNum,
                })),
            ).subscribe((re: { data: Array<any>; more: boolean }) => {
                this.preResult.data = this.preResult.data.concat(re.data);
                this.preResult.more = re.more;
                this.observable.next({
                    data: this.preResult.data,
                    more: re.more,
                });
                if (!re.more) {
                    this.observable.complete();
                }
            });
        }
    }

    breakOff(): void {
        if (this.timer) {
            clearTimeout(this.timer);
        }
        this.timer = null;
    }
}

export class AlarmProvider extends AsyncProvider {
    static type = 'alarm';
    preValue = '';

    constructor(request: { (value: any, range?: any): Observable<any>; (value: string, params?: any): Observable<object> }) {
        super();
        this.request = request;
    }

    use = (): boolean => false;

    search(value: string): Observable<unknown> {
        if (this.isSameType(value)) {
            return new Observable((o) => {
                this.observable = o;
                o.next(this.preResult);
            });
        }
        return super.search(value);
    }

    isSameType(value: string): boolean {
        return value.split(' ')[0] === this.preValue.split(' ')[0];
    }
}

export class EventProvider extends AsyncProvider {
    static type = 'event';
    preValue = '';

    constructor(request: { (value: any, range?: any): Observable<any>; (value: string, params?: any): Observable<object> }) {
        super();
        this.request = request;
    }

    use = (): boolean => false;

    search(value: string): Observable<unknown> {
        if (this.isSameType(value)) {
            return new Observable((o) => {
                this.observable = o;
                o.next(this.preResult);
            });
        }
        return super.search(value);
    }

    isSameType(value: string): boolean {
        return value.split(' ')[0] === this.preValue.split(' ')[0];
    }
}
